import { Inject } from '@wendellhu/redi';
import { IHttpServiceToken, type IHttpService } from "@/backend/core";
import type { ICaseRepository } from "../interfaces/case.repository.interface";
import type { CaseMaindataDTO } from "@holoview/dao-gen/generated";
import {
  createTable,
  updateTable,
} from "@/backend/core/clients/nocobase.client";
import type {
  CaseAppendsQueryParams,
  CaseMaindataArchiveCreateParams,
  CaseLibraryQueryParams,
  CaseListPageDto,
  CaseListQueryParams,
  CaseMainDataParams,
  CaseShowcaseQueryParams,
} from "../dto/case.dto";

export class CaseRepository implements ICaseRepository {
  constructor(@Inject(IHttpServiceToken) private readonly http: IHttpService) {}

  async findAll(
    filter: CaseListQueryParams,
  ): Promise<CaseListPageDto<CaseMaindataDTO>> {
    const result = await this.http.get<{
      data?: CaseMaindataDTO[];
      meta?: { count?: number };
    }>("case_maindata:list", {
      filter: JSON.stringify(
        this.buildFilter(filter as unknown as Record<string, unknown>),
      ),
      page: filter.page ?? 1,
      pageSize: filter.pageSize ?? 20,
    });
    return {
      data: result.data ?? [],
      total: Number(result.meta?.count ?? 0) || 0,
    };
  }

  async searchLibraryCases(
    params: CaseLibraryQueryParams,
  ): Promise<CaseListPageDto<Record<string, unknown>>> {
    const result = await this.http.get<{
      data?: Record<string, unknown>[];
      meta?: { count?: number };
    }>("case_maindata:list", {
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
      filter: JSON.stringify(
        this.buildLibraryFilter(params as unknown as Record<string, unknown>),
      ),
      "appends[]": [
        "hospital",
        "hospital_department",
        "doctor",
        "site",
        "extend",
        "factors",
        "extend.bodypart",
        "extend.indication",
        "extend.treatment_plan",
        "createdBy",
        "updatedBy",
      ],
      "sort[]": ["-updatedAt"],
    });
    return {
      data: result.data ?? [],
      total: Number(result.meta?.count ?? 0) || 0,
    };
  }

  async findById(id: number): Promise<CaseMaindataDTO | null> {
    const result = await this.http.get<{ data: CaseMaindataDTO[] }>(
      "case_maindata:list",
      {
        filter: JSON.stringify({ id: { $eq: id } }),
      },
    );
    return result.data.length ? result.data[0] : null;
  }

  async findEditableCase(id: number): Promise<Record<string, unknown> | null> {
    const appends = [
      "hospital",
      "hospital_department",
      "hospital_department_category",
      "doctor",
      "site",
      "extend",
      "extend.bodypart",
      "extend.indication",
      "extend.treatment_plan",
    ];
    const result = await this.http.get<{ data?: Record<string, unknown>[] }>(
      "case_maindata:list",
      {
        page: 1,
        pageSize: 1,
        filter: JSON.stringify({ id: { $eq: id } }),
        "appends[]": appends,
      },
    );
    return result.data?.[0] ?? null;
  }

  async createMainData(params: CaseMainDataParams): Promise<CaseMaindataDTO> {
    const res = await createTable<CaseMaindataDTO>(
      "case_maindata",
      params as unknown as Record<string, unknown>,
      "",
    );
    return res.data;
  }

  async updateMainData(id: number, params: CaseMainDataParams): Promise<void> {
    await updateTable<CaseMaindataDTO>(
      "case_maindata",
      id,
      params as unknown as Record<string, unknown>,
    );
  }

  async createArchive(
    caseId: number,
    params: CaseMaindataArchiveCreateParams,
  ): Promise<void> {
    await this.http.post(
      `case_maindata/${caseId}/extend:create`,
      params as Record<string, unknown>,
    );
  }

  async updateArchive(
    caseId: number,
    params: CaseMaindataArchiveCreateParams,
  ): Promise<void> {
    const existing = await this.http.get<{ data?: Array<{ id?: number | string }> }>(
      "case_maindata_archive:list",
      {
        page: 1,
        pageSize: 1,
        filter: JSON.stringify({ case_maindata_id: { $eq: caseId } }),
      },
    );
    const archiveId = Number(existing.data?.[0]?.id ?? 0) || 0;
    if (!archiveId) {
      await this.createArchive(caseId, params);
      return;
    }

    await updateTable(
      "case_maindata_archive",
      archiveId,
      params as unknown as Record<string, unknown>,
    );
  }

  async updateStatus(id: number, status: string): Promise<void> {
    await updateTable<CaseMaindataDTO>("case_maindata", id, {
      status,
    });
  }

  async searchWithAppends(
    params: CaseAppendsQueryParams,
  ): Promise<CaseMaindataDTO[]> {
    const filterStr = params.filter ? JSON.stringify(params.filter) : undefined;
    const result = await this.http.get<{ data: CaseMaindataDTO[] }>(
      "case_maindata:list",
      {
        ...params,
        filter: filterStr,
      },
    );
    return result.data ?? [];
  }

  async getShowcaseCaseCards(
    params: CaseShowcaseQueryParams,
  ): Promise<Record<string, unknown>[]> {
    const appends = [
      "hospital",
      "hospital_department",
      "doctor",
      "site",
      "extend",
      "extend.bodypart",
      "extend.indication",
      "extend.treatment_plan",
      "createdBy",
      "updatedBy",
    ];
    const result = await this.http.get<{ data: Record<string, unknown>[] }>(
      "case_maindata:list",
      {
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 5,
        "appends[]": appends,
        "sort[]": ["-updatedAt"],
      },
    );
    return result.data ?? [];
  }

  async findCasesWithArchiveByIds(
    ids: number[],
  ): Promise<Record<string, unknown>[]> {
    const missing = [...new Set(ids)].filter((id) => id > 0);
    if (missing.length === 0) return [];
    const appends = [
      "hospital",
      "hospital_department",
      "doctor",
      "site",
      "extend",
      "extend.bodypart",
      "extend.indication",
      "extend.treatment_plan",
      "createdBy",
      "updatedBy",
    ];
    const filter = JSON.stringify({ id: { $in: missing.map(String) } });
    const result = await this.http.get<{ data: Record<string, unknown>[] }>(
      "case_maindata:list",
      {
        page: 1,
        pageSize: Math.min(2000, Math.max(50, missing.length)),
        filter,
        "appends[]": appends,
      },
    );
    return result.data ?? [];
  }

  private buildFilter(
    filter: Record<string, unknown>,
  ): Record<string, unknown> {
    const conditions: Record<string, unknown>[] = [];
    if (filter.keyword)
      conditions.push({ name: { $includes: filter.keyword } });
    if (filter.hospitalId)
      conditions.push({ ga_hospital_id: { $eq: filter.hospitalId } });
    if (filter.departmentId)
      conditions.push({
        ga_hospital_department_id: { $eq: filter.departmentId },
      });
    return conditions.length ? { $and: conditions } : {};
  }

  private buildLibraryFilter(
    filter: Record<string, unknown>,
  ): Record<string, unknown> {
    const conditions: Record<string, unknown>[] = [];
    if (filter.keyword)
      conditions.push({ name: { $includes: filter.keyword } });
    if (filter.hospitalId)
      conditions.push({ ga_hospital_id: { $eq: filter.hospitalId } });
    if (filter.departmentId)
      conditions.push({
        ga_hospital_department_id: { $eq: filter.departmentId },
      });
    if (filter.doctorId)
      conditions.push({ ga_doctor_id: { $eq: filter.doctorId } });
    if (filter.siteId) conditions.push({ ga_site_id: { $eq: filter.siteId } });
    return conditions.length ? { $and: conditions } : {};
  }
}
